import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import localization from 'assets/localization/localization';
import DocumentView from 'components/sales/downloadDocView/documentView';
import {
  DivLayoutV2NestedSection,
  DivLayoutV2FieldGrid,
  DivLayoutV2SubCard,
} from 'components/UI';
import {
  INVOICING_FREQUENCY,
  HARDWARE_TAGS_FREQUENCY,
  CONSUMABLES_FREQUENCY,
  SINGLE_LINE_ITEMS,
  SINGLE_LINE_SOL_ITEMS,
  SOLUTION_BOQ_ITEMS,
  CONSUMABLE_BOQ_ITEMS,
  CONSUMABLE_LINE_ITEMS,
  MILESTONES,
} from 'components/sales/formSection/materialHardware/constants/invoiceConstants';
import 'assets/css/info-tooltip.css';
import './CapexInvoicingDetails.css';

const display = (val) =>
  val != null && val !== '' ? String(val) : localization.na;

const formatRupee = (val) => `₹ ${parseFloat(val || 0).toFixed(2)}`;

const INVOICE_LINE_ITEM_LABELS = [
  ...SINGLE_LINE_ITEMS,
  ...SINGLE_LINE_SOL_ITEMS,
  ...SOLUTION_BOQ_ITEMS,
  ...CONSUMABLE_BOQ_ITEMS,
  ...CONSUMABLE_LINE_ITEMS,
  ...MILESTONES,
].reduce((acc, item) => {
  acc[item.id] = item.name;
  return acc;
}, {});

const resolveSelectedLabels = (selected) => {
  if (!Array.isArray(selected) || !selected.length) return null;
  const names = selected.map((id) => INVOICE_LINE_ITEM_LABELS[Number(id)] ?? `ID ${id}`);
  return names.join(', ');
};

const normalizeInvoiceLineItems = (invoice) => {
  if (!invoice) return [];
  if (Array.isArray(invoice)) return invoice;
  return Object.values(invoice);
};

const getLineItemLabel = (item) => {
  if (item.materials?.length) {
    const materials = Array.isArray(item.materials) ? item.materials : [item.materials];
    return materials.filter(Boolean).join(', ');
  }
  if (typeof item.materials === 'string' && item.materials) return item.materials;
  return resolveSelectedLabels(item.selected);
};

const resolveLabel = (list, id, value) => {
  if (value != null && value !== '') return String(value);
  const match = list.find((item) => item.id === Number(id));
  return match?.name ?? null;
};

export const getCapexInvoicingDetails = (contract) => {
  const raw =
    contract?.capex_invoicing_details ??
    contract?.capex?.capex_invoicing_module ??
    contract?.capex?.capex_invoicing_details;

  if (!raw || Array.isArray(raw) || typeof raw !== 'object') return null;
  return Object.keys(raw).length > 0 ? raw : null;
};

const hasInvoiceMetaContent = (entry) =>
  entry &&
  (entry.invoice_number ||
    entry.pdf_link ||
    entry.invoice_link ||
    entry.invoice_type);

const normalizeInvoiceType = (type) => {
  if (type == null || type === '') return '';
  const upper = String(type).toUpperCase();
  if (upper === 'PI') return 'PI';
  if (upper === 'TI') return 'TI';
  return String(type);
};

const getInvoiceDocHeading = (type, suffix = 'No.') => {
  const normalized = normalizeInvoiceType(type);
  if (normalized === 'PI') return `PI ${suffix}`;
  if (normalized === 'TI') return `TI ${suffix}`;
  if (type) return `${type} ${suffix}`;
  return `Invoice ${suffix}`;
};

/** Collect PI / TI (and other) invoice meta entries from API shapes. */
export const collectInvoiceMetaEntries = (invoicing) => {
  if (!invoicing) return [];

  const entries = [];
  const seen = new Set();

  const addEntry = (entry, defaultType) => {
    if (!hasInvoiceMetaContent(entry)) return;
    const invoiceType = normalizeInvoiceType(
      entry.invoice_type ?? defaultType ?? ''
    );
    const key = invoiceType || `entry-${entries.length}`;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({
      ...entry,
      invoice_type: invoiceType || entry.invoice_type,
      pdf_link: entry.pdf_link ?? entry.invoice_link,
    });
  };

  const meta = invoicing.meta_data;

  if (Array.isArray(meta)) {
    meta.forEach((item) => addEntry(item));
  } else if (meta && typeof meta === 'object') {
    if (meta.PI || meta.pi) addEntry({ ...(meta.PI || meta.pi), invoice_type: 'PI' }, 'PI');
    if (meta.TI || meta.ti) addEntry({ ...(meta.TI || meta.ti), invoice_type: 'TI' }, 'TI');
    if (hasInvoiceMetaContent(meta)) addEntry(meta);
  }

  if (invoicing.pi_meta_data) addEntry({ ...invoicing.pi_meta_data, invoice_type: 'PI' }, 'PI');
  if (invoicing.ti_meta_data) addEntry({ ...invoicing.ti_meta_data, invoice_type: 'TI' }, 'TI');

  if (invoicing.pi_number || invoicing.pi_pdf_link || invoicing.pi_link) {
    addEntry(
      {
        invoice_type: 'PI',
        invoice_number: invoicing.pi_number ?? invoicing.pi_invoice_number,
        pdf_link: invoicing.pi_pdf_link ?? invoicing.pi_link,
      },
      'PI'
    );
  }
  if (invoicing.ti_number || invoicing.ti_pdf_link || invoicing.ti_link) {
    addEntry(
      {
        invoice_type: 'TI',
        invoice_number: invoicing.ti_number ?? invoicing.ti_invoice_number,
        pdf_link: invoicing.ti_pdf_link ?? invoicing.ti_link,
      },
      'TI'
    );
  }

  return entries;
};

const InvoiceLineItems = ({ invoice, variant }) => {
  const items = normalizeInvoiceLineItems(invoice);

  if (!items.length) return <p className="mb-0">{localization.na}</p>;

  if (variant === 'milestone') {
    return items.map((item, itemIdx) => (
      <DivLayoutV2SubCard
        key={itemIdx}
        badge={getLineItemLabel(item) || `Item ${itemIdx + 1}`}
        fields={[
          { heading: 'Amount', data: `${display(item.amount)} %` },
          { heading: 'Specification', data: display(item.description ?? item.desc) },
        ]}
      />
    ));
  }

  if (variant === 'retention') {
    return items.map((item, itemIdx) => (
      <DivLayoutV2SubCard
        key={itemIdx}
        badge={`Retention ${itemIdx + 1}`}
        fields={[
          { heading: 'Description', data: display(item.desc) },
          { heading: 'Amount', data: `${display(item.percentage)} %` },
          { heading: 'Months', data: display(item.months) },
          { heading: 'Stage', data: display(item.post_stage_value) },
        ]}
      />
    ));
  }

  return items.map((item, itemIdx) => (
    <DivLayoutV2SubCard
      key={itemIdx}
      badge={getLineItemLabel(item) || `Line ${itemIdx + 1}`}
      fields={[
        {
          heading: 'Amount',
          data:
            item.amount != null && item.amount !== ''
              ? `${display(item.amount)} %`
              : localization.na,
        },
        { heading: 'Specification', data: display(item.description ?? item.desc) },
      ]}
    />
  ));
};

InvoiceLineItems.propTypes = {
  invoice: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  variant: PropTypes.oneOf(['single', 'milestone', 'retention']).isRequired,
};

const CapexInvoicingDetails = ({ contract, retention = [] }) => {
  const invoicing = getCapexInvoicingDetails(contract);
  const [openSections, setOpenSections] = useState({});

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!invoicing) return null;

  const commercial = contract || {};
  const isSingleTime = Number(invoicing.invoice_frequency_id) === 1;
  const invoiceFrequency = resolveLabel(
    INVOICING_FREQUENCY,
    invoicing.invoice_frequency_id,
    invoicing.invoice_frequency_value
  );
  const hardwareNomenclature = resolveLabel(
    HARDWARE_TAGS_FREQUENCY,
    invoicing.invoice_nomenclature_id,
    invoicing.invoice_nomenclature_value
  );
  const consumableNomenclature = resolveLabel(
    CONSUMABLES_FREQUENCY,
    invoicing.invoice_consumable_boq_id,
    invoicing.invoice_consumable_boq_value
  );
  const invoicesItems = (invoicing.invoices_items || invoicing.line_items || []).filter(
    (invoice) => normalizeInvoiceLineItems(invoice).length > 0
  );
  const invoiceMetaEntries = collectInvoiceMetaEntries(invoicing);

  const amountFields = [
    { heading: localization.contractAmount, data: formatRupee(invoicing.total_amount) },
    {
      heading: localization.hardwareAmount,
      data: formatRupee(
        parseFloat(commercial.hardware_amount || 0) +
          parseFloat(commercial.software_amount || 0)
      ),
    },
    { heading: localization.cartage, data: formatRupee(commercial.cartage_amount) },
    {
      heading: localization.capexIntstallationAmount,
      data: formatRupee(commercial.installation_amount),
    },
    { heading: localization.tagsAmount, data: formatRupee(commercial.tags_amount) },
  ];

  const configFields = [
    { heading: localization.invoiceFrequency, data: display(invoiceFrequency) },
    ...(invoicing.invoice_count != null
      ? [{ heading: 'Invoice Count', data: display(invoicing.invoice_count) }]
      : []),
    ...(isSingleTime
      ? [
          { heading: localization.hardwareFrequency, data: display(hardwareNomenclature) },
          { heading: localization.consumbaleFrequency, data: display(consumableNomenclature) },
        ]
      : [
          {
            heading: localization.raBills,
            data: invoicing.is_ra_bills === 'yes' ? 'Yes' : 'No',
          },
        ]),
  ];

  if (invoicing.advance_pi != null && invoicing.advance_pi !== '') {
    configFields.push(
      { heading: 'Advance PI', data: invoicing.advance_pi === 'yes' ? 'Yes' : 'No' },
      { heading: 'Advance PI Amount', data: formatRupee(invoicing.advance_pi_amount) },
      { heading: 'Advance PI Description', data: display(invoicing.advance_pi_desc), plainValue: true }
    );
  }

  if (invoicing.convert_pi_ti != null && invoicing.convert_pi_ti !== '') {
    configFields.push({
      heading: localization.capex_convert_pi_ti || 'Convert PI to TI',
      data: invoicing.convert_pi_ti === 'yes' ? 'Yes' : 'No',
    });
  }

  return (
    <DivLayoutV2NestedSection title={localization.invoiceModule}>
      {invoiceMetaEntries.some((entry) => entry.pdf_link) && (
        <div className="site-detail-invoice-actions">
          {invoiceMetaEntries
            .filter((entry) => entry.pdf_link)
            .map((entry) => {
              const type = normalizeInvoiceType(entry.invoice_type);
              return (
                <a
                  key={`view-${type || entry.invoice_number}`}
                  href={entry.pdf_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-success site-detail-invoice-action-btn"
                >
                  View {type || 'Invoice'}
                </a>
              );
            })}
        </div>
      )}

      <DivLayoutV2FieldGrid fields={amountFields} />
      <DivLayoutV2FieldGrid fields={configFields} />

      {invoicesItems.length > 0 &&
        (isSingleTime ? (
          invoicesItems.map((invoice, index) => {
            const key = `invoice-${index}`;
            return (
              <div key={key} className="site-detail-invoice-accordion">
                <button
                  type="button"
                  className="site-detail-invoice-accordion-header"
                  onClick={() => toggleSection(key)}
                >
                  <span>
                    # {localization.invoiceNo} {index + 1}
                  </span>
                  <span>{openSections[key] ? '−' : '+'}</span>
                </button>
                {openSections[key] && (
                  <div className="site-detail-invoice-accordion-body">
                    <InvoiceLineItems invoice={invoice} variant="single" />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          invoicesItems.map((invoice, index) => {
            const milestoneKey = `invoice-milestone-${index}`;
            const retentionKey = `invoice-retention-${index}`;
            return (
              <React.Fragment key={`milestone-invoice-${index}`}>
                <div className="site-detail-invoice-accordion">
                  <button
                    type="button"
                    className="site-detail-invoice-accordion-header"
                    onClick={() => toggleSection(milestoneKey)}
                  >
                    <span>
                      # {localization.milestoneNo} {index + 1}
                    </span>
                    <span>{openSections[milestoneKey] ? '−' : '+'}</span>
                  </button>
                  {openSections[milestoneKey] && (
                    <div className="site-detail-invoice-accordion-body">
                      <InvoiceLineItems invoice={invoice} variant="milestone" />
                    </div>
                  )}
                </div>
                {retention.length > 0 && (
                  <div className="site-detail-invoice-accordion">
                    <button
                      type="button"
                      className="site-detail-invoice-accordion-header"
                      onClick={() => toggleSection(retentionKey)}
                    >
                      <span># {localization.retentionInvoice}</span>
                      <span>{openSections[retentionKey] ? '−' : '+'}</span>
                    </button>
                    {openSections[retentionKey] && (
                      <div className="site-detail-invoice-accordion-body">
                        <InvoiceLineItems invoice={retention} variant="retention" />
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })
        ))}

      {invoiceMetaEntries.length > 0 &&
        invoiceMetaEntries.map((entry, index) => {
          const type = normalizeInvoiceType(entry.invoice_type);
          return (
            <DivLayoutV2SubCard
              key={`${type || 'invoice'}-${index}`}
              badge={type || `Invoice ${index + 1}`}
              fields={[
                {
                  heading: getInvoiceDocHeading(type, 'No.'),
                  data: display(entry.invoice_number),
                },
              ]}
            >
              {entry.pdf_link && (
                <DocumentView
                  title={`${getInvoiceDocHeading(type, 'link')}`}
                  attachment={entry.pdf_link}
                />
              )}
            </DivLayoutV2SubCard>
          );
        })}
    </DivLayoutV2NestedSection>
  );
};

CapexInvoicingDetails.propTypes = {
  contract: PropTypes.object.isRequired,
  retention: PropTypes.array,
};

export default CapexInvoicingDetails;
